import { useEffect, useId, useRef, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ImageOff, Images, Upload } from 'lucide-react'
import { ActivityIndicator, AdvancedDisclosure, Button, Dialog, Field, Input, StatePanel } from '@/components/common'
import { mediaUploadContentType, mediaUploadKind, validateMediaUploadFile } from '@/components/post-creator/mediaAttachments'
import { mediaLibraryApi } from '@/services/api'
import { captureVideoPoster } from './videoPoster'
import styles from './nativeAssetControl.module.css'

const HTTPS_ASSET_URL_PATTERN = /^https:\/\/[^\s]+$/

const UPLOAD_ACCEPT = {
  video: 'video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v,.webm',
  image: 'image/png,image/jpeg,image/gif,image/webp,image/avif',
}

function usableURL(url) { return typeof url === 'string' && HTTPS_ASSET_URL_PATTERN.test(url) && url.length <= 2000 }

function uploadErrorMessage(error) {
  if (error?.response?.status === 403) return 'You do not have permission to upload media for this organization.'
  const data = error?.response?.data
  return data?.error?.message || (typeof data?.error === 'string' ? data.error : null) || error?.message || 'The upload could not be completed.'
}

function MediaPreview({ url, type, label, local = false }) {
  const [failed, setFailed] = useState(false)
  if ((!local && !usableURL(url)) || failed) return <div className={styles.unavailable} role="img" aria-label={`Preview unavailable for ${label}`}><ImageOff size={24} aria-hidden="true" /><span>Preview unavailable</span></div>
  return type === 'video'
    ? <video className={styles.preview} src={url} controls preload="metadata" playsInline onError={() => setFailed(true)} aria-label={label} />
    : <img className={styles.preview} src={url} alt={label} onError={() => setFailed(true)} />
}

function AssetLibraryDialog({ slug, type, label, onChoose, onClose }) {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const assets = useInfiniteQuery({
    queryKey: ['native-asset-library', slug, type, query],
    queryFn: ({ pageParam = null }) => mediaLibraryApi.listAssets(slug, {
      type,
      source: 'library',
      include_associations: false,
      ...(pageParam ? { cursor: pageParam } : {}),
      ...(query ? { search: query } : {}),
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: Boolean(slug),
    retry: false,
  })
  const items = assets.data?.pages?.flatMap((page) => page?.items || []) || []
  const forbidden = assets.error?.response?.status === 403
  return <Dialog open title={`Choose ${label.toLowerCase()}`} onClose={onClose} size="large">
    <form className={styles.search} onSubmit={(event) => { event.preventDefault(); setQuery(search.trim()) }}>
      <Field label="Search library"><Input value={search} onChange={(event) => setSearch(event.target.value)} maxLength={120} placeholder="Name or filename" /></Field>
      <Button type="submit" variant="secondary">Search</Button>
    </form>
    {assets.isLoading ? <StatePanel state="loading" title="Loading library" compact />
      : assets.isError && !items.length ? <StatePanel state="error" title={forbidden ? 'Library access unavailable' : 'Could not load the library'} description={forbidden ? 'Ask an administrator for access to this organization’s media library.' : 'Your current media is unchanged. Try again when the connection is restored.'} action={!forbidden ? <Button variant="secondary" onClick={() => assets.refetch()}>Retry</Button> : undefined} compact />
        : !items.length ? <StatePanel state="empty" title={query ? 'No matching media' : 'No saved media'} description={query ? 'Try another name or filename.' : 'Add media in the organization’s media library, then reopen this picker.'} compact />
          : <>
            <div className={styles.grid}>{items.map((asset) => {
              const name = asset.title || asset.metadata?.original_filename || 'Untitled media'
              const ready = asset.asset_type === type && asset.status === 'ready' && usableURL(asset.url)
              return <article key={asset.id} className={styles.asset}>
                <MediaPreview key={asset.url} url={asset.url} type={type} label={name} />
                <strong>{name}</strong>
                {!ready ? <span className={styles.message}>This media is not ready to use.</span> : null}
                <Button size="compact" variant="secondary" disabled={!ready} onClick={() => onChoose(asset.url)}>Use {name}</Button>
              </article>
            })}</div>
            {assets.hasNextPage || assets.isFetchNextPageError || (assets.isError && items.length) ? (
              <div className={styles.loadMoreContainer}>
                {assets.isFetchNextPageError || assets.isError ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="compact"
                    onClick={() => assets.fetchNextPage()}
                  >
                    Retry loading more
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="compact"
                    loading={assets.isFetchingNextPage}
                    disabled={assets.isFetchingNextPage}
                    onClick={() => assets.fetchNextPage()}
                  >
                    Load more
                  </Button>
                )}
              </div>
            ) : null}
          </>}
  </Dialog>
}

// The upload is the same resumable media-library upload a Feed post uses, so
// the clip lands in the organization's library and the card gets its public
// URL. A video also gets its poster captured from the file and uploaded as an
// image, so the card is complete after one pick; the poster control still lets
// the operator replace it.
function useAssetUpload({ slug, field, type, onChange }) {
  const [upload, setUpload] = useState(null)
  const [local, setLocal] = useState(null)
  const generation = useRef(0)
  const objectURL = useRef(null)
  const replaceObjectURL = (next) => {
    if (objectURL.current) URL.revokeObjectURL(objectURL.current)
    objectURL.current = next
  }
  useEffect(() => () => { generation.current += 1; replaceObjectURL(null) }, [])

  const start = async (file) => {
    if (!file) return
    const kind = mediaUploadKind(file)
    const invalid = kind !== type ? `Choose ${type === 'video' ? 'a video' : 'an image'} file.` : validateMediaUploadFile(file)
    if (invalid) { setUpload({ status: 'failed', name: file.name, error: invalid }); return }
    const run = ++generation.current
    const live = () => generation.current === run
    const previewUrl = URL.createObjectURL(file)
    replaceObjectURL(previewUrl)
    setLocal({ previewUrl, url: null })
    setUpload({ status: 'uploading', name: file.name, progress: 0, startedAt: Date.now() })
    // The resumable transfer cannot be aborted mid-flight, so an upload that
    // finishes after this control moved on (unmount, a newer pick) discards
    // its asset instead of leaving an orphan in the library — the same
    // handling PostCreator applies to a removed placeholder.
    const discard = (asset) => { if (asset?.id) mediaLibraryApi.deleteAsset(slug, asset.id).catch(() => {}) }
    try {
      const asset = await mediaLibraryApi.uploadAsset(slug, file, {
        title: file.name, asset_type: kind, content_type: mediaUploadContentType(file), save_to_library: true,
      }, {
        onProgress: (progress) => { if (live()) setUpload((current) => current && { ...current, progress, status: progress >= 100 ? 'finalizing' : 'uploading' }) },
      })
      if (!live()) { discard(asset); return }
      if (!asset?.url) throw new Error('The upload did not return a media URL.')
      setLocal({ previewUrl, url: asset.url })
      onChange(field.path, asset.url)
      if (type !== 'video') { setUpload(null); return }
      // A replaced video must not keep the previous clip's poster, whether or
      // not the capture below succeeds.
      onChange('poster_url', '')
      setUpload((current) => current && { ...current, status: 'poster' })
      try {
        const posterFile = await captureVideoPoster(file)
        if (!live()) return
        const poster = await mediaLibraryApi.uploadAsset(slug, posterFile, {
          title: posterFile.name, asset_type: 'image', content_type: 'image/jpeg', save_to_library: true,
        })
        if (!live()) { discard(poster); return }
        if (!poster?.url) throw new Error('The poster upload did not return an image URL.')
        onChange('poster_url', poster.url)
        setUpload(null)
      } catch (error) {
        if (live()) setUpload({ status: 'poster_failed', name: file.name, error: uploadErrorMessage(error) })
      }
    } catch (error) {
      if (!live()) return
      setLocal(null)
      setUpload({ status: 'failed', name: file.name, error: uploadErrorMessage(error) })
    }
  }
  return { upload, local, start, dismiss: () => setUpload(null) }
}

function UploadStatus({ upload, onDismiss }) {
  if (!upload) return null
  const dismiss = <Button size="compact" variant="ghost" onClick={onDismiss}>Dismiss</Button>
  switch (upload.status) {
    case 'uploading':
      return <ActivityIndicator compact status="running" label={`Uploading ${upload.name}`} detail={`${upload.progress}%`} progress={upload.progress} startedAt={upload.startedAt} />
    case 'finalizing':
      return <ActivityIndicator compact status="processing" label={`Finalizing ${upload.name}`} progress={100} startedAt={upload.startedAt} />
    case 'poster':
      return <ActivityIndicator compact status="processing" label="Capturing poster" detail="The video is saved. A poster frame is being taken from it." startedAt={upload.startedAt} />
    case 'poster_failed':
      return <ActivityIndicator compact status="failed" label="Video saved without a poster" detail={`${upload.error} Choose a poster image below.`} actions={dismiss} />
    default:
      return <ActivityIndicator compact status="failed" label={`Upload failed: ${upload.name}`} detail={upload.error} actions={dismiss} />
  }
}

// `preview={false}` leaves the picture out, for a caller that already shows it. `error` is the URL's
// problem: the URL field opens, is marked invalid and says why. `uploadUnavailable` and `libraryUnavailable`
// are why the operator can't upload or choose from the media library (the API would refuse): that action is
// disabled and the reason shows under the actions before anything is clicked. The URL field stays.
export function NativeAssetControl({ slug, field, value, disabled, preview = true, error = null, uploadUnavailable = null, libraryUnavailable = null, onChange, onBusyChange }) {
  const reasonId = useId()
  const [open, setOpen] = useState(false)
  // The URL field opens when its problem shows, and stays open while it is there, so the focus can land in it.
  const [urlOpen, setUrlOpen] = useState(false)
  if (error && !urlOpen) setUrlOpen(true)
  const type = field.path === 'video_url' ? 'video' : 'image'
  const fileInput = useRef(null)
  const { upload, local, start, dismiss } = useAssetUpload({ slug, field, type, onChange })
  const busy = Boolean(upload && upload.status !== 'failed' && upload.status !== 'poster_failed')
  // A caller can wait for the uploaded URL before saving. Unmounting or a
  // failed upload releases the guard even when no URL is delivered.
  useEffect(() => {
    if (!busy) return undefined
    onBusyChange?.(true)
    return () => onBusyChange?.(false)
  }, [busy, onBusyChange])
  const canUpload = !disabled && Boolean(slug) && !busy && !uploadUnavailable
  const canBrowse = !disabled && Boolean(slug) && !busy && !libraryUnavailable
  const reasons = [uploadUnavailable, libraryUnavailable].filter(Boolean)
  // The file just picked keeps previewing from the local copy: it is what was
  // uploaded, it needs no second download, and it shows while the upload runs.
  const previewLocal = local && (local.url === null ? upload?.status === 'uploading' || upload?.status === 'finalizing' : local.url === value)
  const previewUrl = previewLocal ? local.previewUrl : value
  const pick = (file) => { if (canUpload && file) start(file) }
  const noun = field.label.toLowerCase()
  return <div
    className={styles.control}
    onDragOver={(event) => { if (canUpload) { event.preventDefault(); event.dataTransfer.dropEffect = 'copy' } }}
    onDrop={(event) => { if (!canUpload) return; event.preventDefault(); pick(event.dataTransfer.files?.[0]) }}
  >
    <span className={styles.label}>{field.label}</span>
    {preview && previewUrl ? <MediaPreview key={previewUrl} url={previewUrl} type={type} label={`Current ${noun}`} local={Boolean(previewLocal)} /> : null}
    <UploadStatus upload={upload} onDismiss={dismiss} />
    <div className={styles.actions}>
      <input ref={fileInput} type="file" accept={UPLOAD_ACCEPT[type]} hidden aria-label={`Upload ${noun} file`} disabled={!canUpload} onChange={(event) => { pick(event.target.files?.[0]); event.target.value = '' }} />
      <Button size="compact" variant="secondary" disabled={!canUpload} aria-describedby={uploadUnavailable ? reasonId : undefined} onClick={() => fileInput.current?.click()}><Upload size={14} aria-hidden="true" />Upload {noun}</Button>
      <Button size="compact" variant="ghost" disabled={!canBrowse} aria-describedby={libraryUnavailable ? reasonId : undefined} onClick={() => setOpen(true)}><Images size={14} aria-hidden="true" />{value ? 'Replace from library' : 'Choose from library'}</Button>
      {value && !field.required ? <Button size="compact" variant="ghost" disabled={disabled || busy} onClick={() => onChange(field.path, '')}>Remove {noun}</Button> : null}
    </div>
    {reasons.length ? <p id={reasonId} className={styles.message}>{reasons.join(' ')}</p> : null}
    <AdvancedDisclosure label={`${field.label} URL`} open={urlOpen} onToggle={setUrlOpen}>
      <Field label={`${field.label} URL`} help={field.help} error={error || undefined}><Input size="compact" type="url" value={value || ''} maxLength={2000} disabled={disabled || busy} placeholder="https://" onChange={(event) => onChange(field.path, event.target.value.trim())} /></Field>
    </AdvancedDisclosure>
    {open && !disabled && !libraryUnavailable ? <AssetLibraryDialog key={`${slug}:${field.path}`} slug={slug} type={type} label={field.label} onClose={() => setOpen(false)} onChoose={(url) => { onChange(field.path, url); setOpen(false) }} /> : null}
  </div>
}
